import { useMemo } from 'react';
import SpeechButton from './SpeechButton.jsx';
import { getBestScore, scorePercent } from '../lib/progress.js';

const LEVELS = [
  ['all', 'All'],
  ['beginner', 'Beginner'],
  ['intermediate', 'Intermediate'],
  ['advanced', 'Advanced'],
];

export default function HeadwordDirectory({
  theme,
  subtopic,
  headwords,
  progress,
  level,
  onLevelChange,
  onOpen,
  onBack,
}) {
  const availableLevels = useMemo(
    () => LEVELS.filter(([id]) => id === 'all' || headwords.some((headword) => headword.level === id)),
    [headwords],
  );
  const activeLevel = availableLevels.some(([id]) => id === level) ? level : 'all';
  const filtered = useMemo(
    () => activeLevel === 'all'
      ? headwords
      : headwords.filter((headword) => headword.level === activeLevel),
    [activeLevel, headwords],
  );

  return (
    <main className="page headword-page">
      <button className="back-button" type="button" onClick={onBack}>← {theme.title}</button>
      <section className="headword-hero">
        <p className="eyebrow coral-eyebrow">{theme.title}</p>
        <h1>{subtopic.title}</h1>
        {subtopic.titleZh && <p lang="zh-Hant">{subtopic.titleZh}</p>}
        <p>Choose a level, then select a headword to begin.</p>
      </section>

      <div
        className="level-tabs"
        style={{ '--level-tab-count': availableLevels.length }}
        role="tablist"
        aria-label="Vocabulary level"
      >
        {availableLevels.map(([id, label]) => (
          <button
            className={`level-tab level-tab-${id} ${activeLevel === id ? 'active' : ''}`}
            type="button"
            role="tab"
            aria-selected={activeLevel === id}
            key={id}
            onClick={() => onLevelChange(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {filtered.length ? (
        <section className="headword-grid">
          {filtered.map((headword, index) => {
            const guided = getBestScore(progress, {
              themeId: theme.id,
              subtopicId: subtopic.id,
              headwordId: headword.id,
              mode: 'guided-practice',
            });
            const building = getBestScore(progress, {
              themeId: theme.id,
              subtopicId: subtopic.id,
              headwordId: headword.id,
              mode: 'sentence-building',
            });
            return (
              <button className="headword-card" type="button" key={headword.id} onClick={() => onOpen(headword.id)}>
                <span className="headword-card-top">
                  <span>
                    <small>HEADWORD {String(index + 1).padStart(2, '0')}</small>
                    <strong>{headword.displayText}</strong>
                    <span>{headword.partOfSpeech}</span>
                  </span>
                  <SpeechButton text={headword.audio?.speechText ?? headword.displayText} label={`Play ${headword.displayText}`} />
                </span>
                <span className="definition-zh" lang="zh-Hant">{headword.definitionZh}</span>
                <span className="dual-gauge dual-gauge-vocabulary" aria-label="Headword progress">
                  <span style={{ width: `${scorePercent(guided) / 2}%` }} />
                  <span style={{ width: `${scorePercent(building) / 2}%` }} />
                </span>
                <span className="dual-gauge-labels">
                  <span>Guided {scorePercent(guided)}%</span>
                  <span>Building {scorePercent(building)}%</span>
                </span>
              </button>
            );
          })}
        </section>
      ) : (
        <p className="empty-state">No {LEVELS.find(([id]) => id === activeLevel)?.[1].toLowerCase()} headwords are available in this subtopic.</p>
      )}
      <div className="headword-legend">
        <span><i className="legend-dot guided-dot" /> Guided Practice</span>
        <span><i className="legend-dot building-dot" /> Sentence Building</span>
      </div>
    </main>
  );
}
